type QueryAction = 'select' | 'insert' | 'update' | 'delete';

type FilterOp =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'not_is_null'
  | 'is_null';

interface QueryFilter {
  column: string;
  op: FilterOp;
  value?: unknown;
}

interface QueryOrder {
  column: string;
  ascending: boolean;
}

interface QueryPayload {
  table: string;
  action: QueryAction;
  select?: string;
  values?: unknown;
  filters?: QueryFilter[];
  orders?: QueryOrder[];
  limit?: number;
  single?: boolean;
  maybeSingle?: boolean;
}

interface QueryResult<T = any> {
  data: T | null;
  error: any;
}

const normalizeApiBaseUrl = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed === '') {
    return '/api';
  }
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
};

const API_BASE_URL = normalizeApiBaseUrl(
  (import.meta as any).env?.VITE_API_BASE_URL || '/api'
);

const API_REQUEST_TIMEOUT_MS = 10000;

const fetchWithTimeout = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_REQUEST_TIMEOUT_MS);

  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
};

class LocalQueryBuilder<T = any> implements PromiseLike<QueryResult<T>> {
  private readonly table: string;
  private action: QueryAction = 'select';
  private selectClause = '*';
  private values: unknown;
  private filters: QueryFilter[] = [];
  private orders: QueryOrder[] = [];
  private limitValue?: number;
  private singleRow = false;
  private maybeSingleRow = false;

  constructor(table: string) {
    this.table = table;
  }

  select(columns = '*'): this {
    this.action = this.action === 'insert' || this.action === 'update' ? this.action : 'select';
    this.selectClause = columns;
    return this;
  }

  insert(values: unknown): this {
    this.action = 'insert';
    this.values = values;
    return this;
  }

  update(values: unknown): this {
    this.action = 'update';
    this.values = values;
    return this;
  }

  delete(): this {
    this.action = 'delete';
    return this;
  }

  eq(column: string, value: unknown): this {
    this.filters.push({ column, op: 'eq', value });
    return this;
  }

  neq(column: string, value: unknown): this {
    this.filters.push({ column, op: 'neq', value });
    return this;
  }

  gt(column: string, value: unknown): this {
    this.filters.push({ column, op: 'gt', value });
    return this;
  }

  gte(column: string, value: unknown): this {
    this.filters.push({ column, op: 'gte', value });
    return this;
  }

  lt(column: string, value: unknown): this {
    this.filters.push({ column, op: 'lt', value });
    return this;
  }

  lte(column: string, value: unknown): this {
    this.filters.push({ column, op: 'lte', value });
    return this;
  }

  contains(column: string, value: unknown): this {
    this.filters.push({ column, op: 'contains', value });
    return this;
  }

  not(column: string, operator: string, value: unknown): this {
    if (operator === 'is' && value === null) {
      this.filters.push({ column, op: 'not_is_null' });
    }
    return this;
  }

  order(column: string, options?: { ascending?: boolean }): this {
    this.orders.push({ column, ascending: options?.ascending ?? true });
    return this;
  }

  limit(value: number): this {
    this.limitValue = value;
    return this;
  }

  single(): this {
    this.singleRow = true;
    return this;
  }

  maybeSingle(): this {
    this.maybeSingleRow = true;
    return this;
  }

  async execute(): Promise<QueryResult<T>> {
    const payload: QueryPayload = {
      table: this.table,
      action: this.action,
      select: this.selectClause,
      values: this.values,
      filters: this.filters,
      orders: this.orders,
      limit: this.limitValue,
      single: this.singleRow,
      maybeSingle: this.maybeSingleRow,
    };

    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}/db-query.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('adminId')
            ? {
                'Admin-Id': localStorage.getItem('adminId') || '',
              }
            : {}),
        },
        body: JSON.stringify(payload),
      });

      const body = await response.json();

      if (!response.ok) {
        return { data: null, error: body.error || { message: 'Request failed' } };
      }

      const rawData = body.data ?? null;

      if ((this.singleRow || this.maybeSingleRow) && Array.isArray(rawData)) {
        if (rawData.length === 0) {
          if (this.singleRow) {
            return {
              data: null,
              error: {
                code: 'PGRST116',
                message: 'JSON object requested, multiple (or no) rows returned',
              },
            };
          }
          return { data: null, error: null };
        }

        if (rawData.length > 1) {
          return {
            data: null,
            error: {
              code: 'PGRST116',
              message: 'JSON object requested, multiple rows returned',
            },
          };
        }

        return {
          data: rawData[0] as T,
          error: body.error ?? null,
        };
      }

      return {
        data: rawData,
        error: body.error ?? null,
      };
    } catch (error) {
      const isTimeoutError = error instanceof DOMException && error.name === 'AbortError';
      return {
        data: null,
        error: {
          message: isTimeoutError
            ? 'Το αίτημα προς τον server έληξε λόγω timeout. Ελέγξτε ότι το API/MySQL τρέχουν.'
            : (error instanceof Error ? error.message : 'Network error'),
        },
      };
    }
  }

  then<TResult1 = QueryResult<T>, TResult2 = never>(
    onfulfilled?: ((value: QueryResult<T>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ): Promise<TResult1 | TResult2> {
    return this.execute().then(onfulfilled ?? undefined, onrejected ?? undefined);
  }
}

class LocalSupabaseClient {
  from<T = any>(table: string): LocalQueryBuilder<T> {
    return new LocalQueryBuilder<T>(table);
  }

  functions = {
    invoke: async (name: string, options?: { body?: any }): Promise<QueryResult<any>> => {
      try {
        const response = await fetchWithTimeout(`${API_BASE_URL}/invoke-function.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name,
            body: options?.body ?? {},
          }),
        });

        const body = await response.json();

        if (!response.ok) {
          return { data: null, error: body.error || { message: 'Function invoke failed' } };
        }

        return {
          data: body.data ?? null,
          error: body.error ?? null,
        };
      } catch (error) {
        const isTimeoutError = error instanceof DOMException && error.name === 'AbortError';
        return {
          data: null,
          error: {
            message: isTimeoutError
              ? 'Το αίτημα προς τον server έληξε λόγω timeout. Ελέγξτε ότι το API/MySQL τρέχουν.'
              : (error instanceof Error ? error.message : 'Network error'),
          },
        };
      }
    },
  };
}

export const supabase = new LocalSupabaseClient();
export const genericSupabase = new LocalSupabaseClient();

export const handleSupabaseError = (error: any, fallbackMessage = 'An error occurred'): string => {
  console.error('Data layer error:', error);
  return error?.message || fallbackMessage;
};

export const getEmailSubjectWithEmoji = (subject: string): string => `🔔 ${subject}`;

export const isAdminAuthenticated = async (): Promise<boolean> => {
  try {
    const adminId = localStorage.getItem('adminId');
    const adminSession = localStorage.getItem('adminSession');

    if (!adminId || !adminSession) {
      return false;
    }

    const { data, error } = await supabase
      .from('administrators')
      .select('active')
      .eq('id', adminId)
      .single();

    return !error && !!data && !!(data as any).active;
  } catch (error) {
    console.error('Admin authentication check error:', error);
    return false;
  }
};

export const adminLogout = (): void => {
  localStorage.removeItem('adminId');
  localStorage.removeItem('adminSession');
  window.location.href = '/admin/login';
};
